// Keep this selection order in sync with AlarmCatalogEntry::Name on the server.
export function alarmName(entry, language, variant = 'full') {
  const locale = String(language ?? 'en')
    .toLowerCase()
    .split(/[-_]/)[0];
  const key = entry?.key || `Alarm ${entry?.id ?? ''}`;
  if (locale === 'keys') return key;
  return entry?.translations?.[locale]?.[variant] || entry?.translations?.en?.[variant] || key;
}

// Legacy alarm keys retain their existing translations, including low/high and
// day/night distinctions that cannot be inferred from a flexible alarm type.
export function alarmDisplayName(alarm, catalog, language, t) {
  const key = alarm.name ?? alarm.id;
  const identity = alarm.identity;
  const entry = identity
    ? catalog?.[identity.alarm_type_id]
    : Object.values(catalog ?? {}).find((candidate) => candidate.key === key);
  const name =
    entry || identity
      ? alarmName(entry ?? { id: identity.alarm_type_id }, language)
      : language === 'keys'
        ? key
        : t(key);
  // Slave offline carries the real Modbus address; other instances are array indexes.
  const indexOffset = Number(identity?.alarm_type_id) === 3000 ? 0 : 1;
  return identity && Number(identity.sub_zone_or_id) !== 255
    ? `${name} #${Number(identity.sub_zone_or_id) + indexOffset}`
    : name;
}

export function alarmValueFormat(key, valueSpec, controller) {
  const isVpd = key === 'climate.vpd' || /^alarm_(low|over)_vpd(_night)?_state$/.test(key);
  const useKpa = isVpd && Number(controller?.settings?.system?.vpd_format) === 1;
  const scale = Number(valueSpec.scale) - (useKpa ? 1 : 0);
  return {
    scale,
    unit: useKpa ? 'kPa' : valueSpec.unit,
    decimals: Math.max(0, -scale),
    // Slider limits are expressed in the catalog's base unit (hPa for VPD).
    unitDivisor: useKpa ? 10 : 1
  };
}

export function formatAlarmValue(value, format) {
  return `${(Number(value) * 10 ** format.scale).toFixed(format.decimals)} ${format.unit}`;
}

function compare_alarm_log_entries_by_timestamp(log_entry_a, log_entry_b) {
  if (log_entry_a['ts'] < log_entry_b['ts']) return -1;
  if (log_entry_a['ts'] > log_entry_b['ts']) return 1;
  return 0;
}

function SortAlarmLog(alarm_log) {
  alarm_log.sort(compare_alarm_log_entries_by_timestamp);
  return alarm_log;
}

export function EditAlarmLog(alarm_log) {
  alarm_log = SortAlarmLog(alarm_log);
  // alarm_log = alarm_log.slice(0,10);
  return alarm_log;
}

export function AddAlarmLogEntry(alarmLog, entry) {
  const previousEntry = [...alarmLog]
    .reverse()
    .find(
      (candidate) =>
        candidate.controller?.id === entry.controller?.id &&
        String(candidate.zone) === String(entry.zone) &&
        candidate.id === entry.id
    );

  if (previousEntry?.triggered !== entry.triggered) alarmLog.push(entry);
  return alarmLog;
}

export function UpdateAlarmLog(curAlarmLog, newAlarmLog, cur_controller) {
  if (!Array.isArray(newAlarmLog)) return EditAlarmLog(curAlarmLog);

  const controllerAlarmLog = [];
  const alarmStates = new Map();

  for (var i = 0; i < newAlarmLog.length; i++) {
    const timestamp = newAlarmLog[i]['timestamp'];
    const zone_id = newAlarmLog[i]['zone_id'];

    AddStructuredAlarmLogEntries(
      controllerAlarmLog,
      newAlarmLog[i].triggered_instances,
      cur_controller,
      timestamp,
      true
    );
    AddStructuredAlarmLogEntries(
      controllerAlarmLog,
      newAlarmLog[i].resolved_instances,
      cur_controller,
      timestamp,
      false
    );

    if ('triggered' in newAlarmLog[i]) {
      const triggeredAlarms = newAlarmLog[i]['triggered'];
      for (let key of Object.keys(triggeredAlarms)) {
        const alarmKey = `${zone_id}|${key}`;
        if (alarmStates.get(alarmKey) !== true) {
          AddAlarmLogEntry(controllerAlarmLog, {
            triggered: true,
            controller: cur_controller,
            id: key,
            ts: timestamp,
            zone: zone_id,
            value: triggeredAlarms[key]
          });
        }
        alarmStates.set(alarmKey, true);
      }
    }
    if ('resolved' in newAlarmLog[i]) {
      const resolvedAlarms = newAlarmLog[i]['resolved'];
      for (let key of Object.keys(resolvedAlarms)) {
        const alarmId = resolvedAlarms[key];
        const alarmKey = `${zone_id}|${alarmId}`;
        if (alarmStates.get(alarmKey) !== false) {
          AddAlarmLogEntry(controllerAlarmLog, {
            triggered: false,
            controller: cur_controller,
            id: alarmId,
            zone: zone_id,
            ts: timestamp
          });
        }
        alarmStates.set(alarmKey, false);
      }
    }
  }

  const otherControllersLog = curAlarmLog.filter(
    (entry) => entry.controller?.id !== cur_controller?.id
  );
  return EditAlarmLog([...otherControllersLog, ...controllerAlarmLog]);
}

export function AddStructuredAlarmLogEntries(alarmLog, instances, controller, timestamp, triggered) {
  // A type can occur on several devices in the same zone. Retain the instance
  // identity for replay suppression and resolve its display name from the catalog.
  for (const instance of instances ?? []) {
    const { zone_id, alarm_type_id, sub_zone_or_id } = instance;
    AddAlarmLogEntry(alarmLog, {
      controller,
      id: `instance:${alarm_type_id}:${sub_zone_or_id}`,
      identity: { zone_id, alarm_type_id, sub_zone_or_id },
      zone: zone_id,
      ts: timestamp,
      triggered,
      ...(triggered ? { value: instance.value } : {})
    });
  }
  return alarmLog;
}

export function MakeAlarmID(controller_id, alarm_id, zone_id) {
  return controller_id + '_' + alarm_id + '|' + zone_id;
}

export function ControllerForAlarm(controllers, alarm) {
  return controllers.find((controller) => alarm.controller_id !== undefined
    ? controller.id === alarm.controller_id
    // Legacy controller-wide alarms omit the |zone suffix in server readback.
    : MakeAlarmID(controller.id, alarm.name, alarm.zone) === alarm.id ||
      `${controller.id}_${alarm.name}` === alarm.id);
}

export function AlarmInList(alarm_id, new_alarms, controller_id, zone_id) {
  for (let i = 0; i < new_alarms.length; i++) {
    if (MakeAlarmID(controller_id, new_alarms[i], zone_id) === alarm_id) {
      return true;
    }
  }
  return false;
}
export function AlarmGetZoneIdAndZonePart(cur_controller, data) {
  const zone_id = 'zone' in data ? data['zone'] : data['id'];
  console.log('[alarms] ZONE_ID', zone_id, data);
  let zone_part = '';
  if (zone_id && zone_id !== '255') {
    if (zone_id === '254') {
      zone_part = ' (global) ';
    } else {
      zone_part = ' (' + (cur_controller.zones?.[zone_id]?.name ?? zone_id) + ') ';
    }
  }
  return [zone_id, zone_part];
}
